import { type IncidentFile, IncidentStatus } from '@piccolohealth/pbs-common';
import { ChakraV3, Empty, FancyDate, FileIcon, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import { showModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useIncidentFilesQuery } from '../../graphql/hooks/useIncidentQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { IncidentFileActionMenu } from './IncidentFileActionMenu';
import { IncidentFileUploadModal } from './IncidentFileUploadModal';

interface Props {
  incidentId: string;
}

export const IncidentFilesCard = (props: Props) => {
  const { incidentId } = props;

  const { organization } = useAppContext();

  const onAddFile = () => {
    showModal(IncidentFileUploadModal, {
      incidentId,
    });
  };

  const { isLoading, error, data } = useIncidentFilesQuery({
    organizationId: organization.id,
    incidentId,
  });

  const files = (data?.organization?.incident?.files as IncidentFile[]) ?? [];
  const isReviewed = data?.organization?.incident?.status === IncidentStatus.Reviewed;

  const content = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (error) {
      return <PiccoloError h='full' error={error} />;
    }

    if (P.isEmpty(files)) {
      return <Empty h='full' title='No files found' p={0} />;
    }

    return (
      <>
        {files.map((file) => (
          <React.Fragment key={file.id}>
            <ChakraV3.HStack align='stretch' fontSize='sm'>
              <FileIcon name={file.name} w={10} h={10} />
              <ChakraV3.Stack gap={0} w='full'>
                <ChakraV3.HStack>
                  <ChakraV3.Text fontWeight='semibold' lineClamp={1}>
                    {file.name}
                  </ChakraV3.Text>
                  <ChakraV3.Spacer />
                  <FancyDate date={file.createdAt} showHumanized={false} color='secondary' />
                </ChakraV3.HStack>
                <ChakraV3.Spacer />
                <ChakraV3.HStack>
                  <ChakraV3.Button
                    onClick={() => window.open(file.viewUrl)}
                    size='xs'
                    variant='plain'
                    w='fit-content'
                  >
                    View
                  </ChakraV3.Button>
                  <ChakraV3.Button
                    onClick={() => window.open(file.downloadUrl)}
                    size='xs'
                    variant='plain'
                    w='fit-content'
                  >
                    Download
                  </ChakraV3.Button>
                  <IncidentFileActionMenu file={file} size='xs' />
                </ChakraV3.HStack>
              </ChakraV3.Stack>
            </ChakraV3.HStack>

            <ChakraV3.Separator />
          </React.Fragment>
        ))}
      </>
    );
  });

  return (
    <ChakraV3.Card.Root h='full'>
      <ChakraV3.Card.Header>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Heading size='md'>Files</ChakraV3.Heading>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Files related to the incident
          </ChakraV3.Text>
        </ChakraV3.Stack>
      </ChakraV3.Card.Header>
      <ChakraV3.Card.Body as={ChakraV3.Stack} gap={4}>
        {content}
        <ChakraV3.Spacer />
        <ChakraV3.HStack w='full'>
          <ChakraV3.Button
            onClick={onAddFile}
            w='full'
            size='sm'
            disabled={isReviewed}
            variant='subtle'
          >
            <FaPlus />
            Add file
          </ChakraV3.Button>
        </ChakraV3.HStack>
      </ChakraV3.Card.Body>
    </ChakraV3.Card.Root>
  );
};
